import { motion } from "framer-motion";
import { CONTACT, STATS } from "@/data/content";

const headline = ["We grow your", "revenue with SEO", "that performs."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.12 },
  }),
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex flex-col justify-center px-6 md:px-12 pt-28 pb-16 overflow-hidden"
    >
      {/* soft radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(0,208,132,0.22), transparent 70%)" }}
      />
      <div className="mx-auto w-full max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-forest">
            SEO Agency · Indore, India
          </span>
          <span className="h-px w-16 bg-hairline" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-ink/50">
            Est. SocialPotato
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display font-extrabold text-ink tracking-tighter leading-[0.92] text-5xl sm:text-7xl md:text-[6.5vw] max-w-[16ch]"
        >
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.06em]">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 1 ? (
                  <>
                    revenue with <span className="text-forest">SEO</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-ink/60 leading-relaxed"
        >
          Rank, convert and scale with SEO strategies that bring measurable results —
          for e-commerce, SaaS and local brands.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={CONTACT.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-book"
            className="group inline-flex items-center gap-3 rounded-full bg-forest px-8 py-4 text-base font-semibold text-white hover:bg-forestdeep transition-colors duration-300"
          >
            Book a strategy call
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">
              →
            </span>
          </a>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-base font-semibold text-ink hover:border-ink hover:bg-ink hover:text-white transition-colors duration-300"
          >
            Chat on WhatsApp
          </a>
        </motion.div>

        {/* stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="mt-20 grid grid-cols-3 gap-px bg-hairline border border-hairline rounded-xl overflow-hidden max-w-2xl"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-paper px-6 py-6" data-testid={`hero-stat-${s.label}`}>
              <div className="font-mono text-3xl md:text-4xl font-bold text-forest tracking-tight">
                {s.value}
              </div>
              <div className="mt-1 text-sm text-ink/50">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
